/**
 * Helper functions for admin analytics optimization
 * Determines when to use common API vs individual product APIs
 */

import { PRODUCT_TYPE_IDS } from "./sf_constants";

// Allowed filters for optimization
const ALLOWED_FILTERS_SET = new Set([
  'account_ids',
  'brand_ids', 
  'campaign_ids',
  'product_type_ids',
  'lead_type_ids',
  'account_manager_ids',
  'sales_manager_ids',
  'insurance_carrier_ids',
  'invoice',
  // admin_account_stats carries a PST-local hour, so the common API answers the
  // day segment and the hours it resolves to on the optimized path too (BM-9333).
  'day_segment',
  'hours_of_day'
]);

// Allowed group bys for optimization
const ALLOWED_GROUP_BYS_SET = new Set([
  'account_id',
  'account_manager_id',
  'sales_manager_id',
  'insurance_carrier_id',
  'campaign_id',
  'brand_id',
  'lead_type_id',
  'product_type_id',
  // Timeframe group bys
  'day_of_week',
  'hour',
  'start_of_day',
  'start_of_week',
  'start_of_month',
  'start_of_quarter',
  'start_of_year'
]);

// Non-filter fields that should be ignored
const NON_FILTER_FIELDS_SET = new Set([
  'start_date', 
  'end_date', 
  'timezone', 
  'group_by_params'
]);

/**
 * Checks if the current filters and group bys qualify for optimization
 * @param {Object} filters - Current filter values
 * @param {Array} groupByParams - Current group by parameters
 * @returns {boolean} - True if optimization can be applied
 */
export const canUseAdminAccountStats = (filters, groupByParams) => {
  // Check if product type is syndi clicks - should not use optimization
  const productTypeIds = filters.product_type_ids;
  if (productTypeIds && Array.isArray(productTypeIds) && productTypeIds.length === 1) {
    if (productTypeIds[0] === PRODUCT_TYPE_IDS.SYNDI_CLICKS) {
      return false;
    }
  }

  // Check if all group bys are allowed
  if (!groupByParams.every(param => ALLOWED_GROUP_BYS_SET.has(param.value))) {
    return false;
  }

  // Check if all filters are allowed or empty or non-filter fields
  return Object.keys(filters).every(key => {
    const value = filters[key];
    return (
      !value ||
      (Array.isArray(value) && value.length === 0) ||
      NON_FILTER_FIELDS_SET.has(key) ||
      ALLOWED_FILTERS_SET.has(key)
    );
  });
};

/**
 * Maps common API response fields to the expected format
 * @param {Object} commonData - Data from common API
 * @returns {Object} - Mapped data with correct field names
 */
export const mapCommonAPIData = (commonData) => {
  if (!commonData) return commonData;

  return {
    ...commonData,
    // Map conversion fields from common API response to expected field names
    conv_stats: parseFloat(commonData.num_conversions || 0),
    conv_cvr: parseFloat(commonData.conversion_rate || 0),
    conv_cpa: parseFloat(commonData.cpa || 0),
    // Map all the fields from the common API response format
    total_inventory: parseFloat(commonData.total_inventory || 0),
    total_opportunities: parseFloat(commonData.total_opportunities || 0),
    total_searches: parseFloat(commonData.total_searches || 0),
    total_selected: parseFloat(commonData.total_selected || 0),
    total_accepted: parseFloat(commonData.total_accepted || 0),
    total_cost: parseFloat(commonData.total_cost || 0),
    total_bid_amt: parseFloat(commonData.total_bid_amt || 0),
    target_rate: parseFloat(commonData.target_rate || 0),
    bid_rate: parseFloat(commonData.bid_rate || 0),
    epw: parseFloat(commonData.epw || 0),
    win_rate: parseFloat(commonData.win_rate || 0),
    success_rate: parseFloat(commonData.success_rate || 0),
    avg_bid: parseFloat(commonData.avg_bid || 0),
    cost_per: parseFloat(commonData.cost_per || 0),
    // Additional fields that might be present
    company_name: commonData.company_name || '',
    account_discarded_at: commonData.account_discarded_at || null
  };
};
